import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, X, CheckCircle2, MessageSquareQuote } from "lucide-react";

/**
 * Modal that asks a recently-completed-project client to leave a 0-5 star review
 * + free-text feedback. Auto-fetches eligible projects on mount; if none, renders nothing.
 *
 * Props:
 *   trigger?: "auto" | "manual"   — when "manual", parent controls visibility via `open`/onClose
 */
export default function TestimonialPrompt() {
  const [eligible, setEligible] = useState([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("rate"); // rate | thanks
  const [project, setProject] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [allowPublish, setAllowPublish] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Delay fetch so it doesn't hijack the initial page paint.
    const timer = setTimeout(async () => {
      try {
        const r = await api.get("/testimonials/eligible");
        if (cancelled) return;
        setEligible(r.data || []);
        if (r.data?.length) {
          // Per-project 30-day cooldown, persisted across sessions.
          const raw = JSON.parse(localStorage.getItem("pb_dismissed_testimonials") || "{}");
          const now = Date.now();
          const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
          const dismissed = typeof raw === "object" && !Array.isArray(raw) ? raw : {};
          const next = r.data.find((p) => {
            const until = dismissed[p.project_id];
            return !until || until < now;
          });
          if (next) {
            setProject(next);
            setOpen(true);
          }
        }
      } catch (err) {
        console.warn("Eligible testimonials fetch failed:", err);
      }
    }, 1500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const dismiss = () => {
    if (project) {
      const raw = JSON.parse(localStorage.getItem("pb_dismissed_testimonials") || "{}");
      const dismissed = typeof raw === "object" && !Array.isArray(raw) ? raw : {};
      dismissed[project.project_id] = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
      localStorage.setItem("pb_dismissed_testimonials", JSON.stringify(dismissed));
    }
    setOpen(false);
    setStep("rate");
    setRating(0);
    setText("");
  };

  const submit = async () => {
    if (rating === 0) { toast.error("Pick a rating first"); return; }
    if (text.trim().length < 4) { toast.error("Add a few words"); return; }
    setSubmitting(true);
    try {
      await api.post("/testimonials", {
        project_id: project.project_id,
        rating,
        text: text.trim(),
        allow_publish: allowPublish,
      });
      setStep("thanks");
      // Remove this project from eligible list
      setEligible((arr) => arr.filter((p) => p.project_id !== project.project_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !project) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" data-testid="testimonial-prompt">
      <div className="bg-white rounded-xl max-w-lg w-full overflow-hidden shadow-2xl">
        <div className="bg-gradient-to-br from-[#0A2540] to-[#103960] p-6 text-white relative">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-white/60 hover:text-white"
            data-testid="testimonial-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 rounded-md bg-[#C9A356]/20 grid place-items-center mb-3">
            <MessageSquareQuote className="w-6 h-6 text-[#C9A356]" />
          </div>
          <h2 className="font-heading text-2xl">{step === "thanks" ? "Thank you!" : "How did we do?"}</h2>
          <p className="text-sm text-slate-300 mt-1">
            {step === "thanks"
              ? "Your feedback has been sent to our team."
              : `Your project "${project.title}" was just marked complete.`}
          </p>
        </div>

        {step === "rate" ? (
          <div className="p-6 space-y-5">
            <div className="text-center">
              <p className="text-sm text-[#475569] mb-3">Tap a star to rate your experience</p>
              <div className="flex justify-center gap-1" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRating(s)}
                    onMouseEnter={() => setHover(s)}
                    onMouseLeave={() => setHover(0)}
                    data-testid={`star-${s}`}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        s <= (hover || rating) ? "text-[#C9A356] fill-[#C9A356]" : "text-slate-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-xs text-[#475569] mt-2">
                  {["", "Not great", "Could be better", "Good", "Great", "Outstanding!"][rating]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-[#475569] mb-1.5 font-bold">Your feedback</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                maxLength={800}
                placeholder="Tell us about your experience…"
                data-testid="testimonial-text"
                className="w-full border border-[#E2E8F0] rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#C9A356]/40 focus:border-[#C9A356] outline-none"
              />
              <div className="text-[10px] text-[#94A3B8] mt-1 text-right">{text.length}/800</div>
            </div>

            <label className="flex items-start gap-2 text-xs text-[#475569] cursor-pointer">
              <input
                type="checkbox"
                checked={allowPublish}
                onChange={(e) => setAllowPublish(e.target.checked)}
                data-testid="allow-publish"
                className="mt-0.5 accent-[#C9A356]"
              />
              <span>Allow PowerBuilt to share this testimonial publicly (with my first name & rating).</span>
            </label>

            <div className="flex gap-3 pt-2">
              <button onClick={dismiss} className="btn-outline-navy text-sm flex-1" data-testid="testimonial-skip">
                Maybe later
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="btn-accent text-sm flex-1"
                data-testid="testimonial-submit"
              >
                {submitting ? "Sending…" : "Submit feedback"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <p className="text-[#475569]">
              We really appreciate you taking the time. The PowerBuilt team will see your feedback right away.
            </p>
            <button onClick={dismiss} className="btn-primary mt-6" data-testid="testimonial-done">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
